import { useEffect } from 'react';
import { create } from 'zustand';
import type { PlanSummary } from '@timato/shared';
import { api } from '@/auth/api';
import { useAuth } from '@/auth/store';

type PlanStore = {
  /** `undefined` until the first answer; there is no "no plan" object, the summary always says what is on. */
  summary: PlanSummary | undefined;
  load: () => Promise<void>;
  reset: () => void;
};

/**
 * The person's standing, mirrored from `/v1/account/plan`. The server decides; this only remembers the last
 * answer so the shell can gate sync and show the right card without asking on every render. Offline, the last
 * answer stands, which is the right call: a lapsed plan cannot sync anyway, and a live one should keep trying.
 */
export const usePlan = create<PlanStore>((set) => ({
  summary: undefined,
  async load() { try { set({ summary: await api<PlanSummary>('/v1/account/plan') }); } catch { /* offline or signed out: keep what we had */ } },
  reset() { set({ summary: undefined }); },
}));

/** Loads on sign-in and forgets on sign-out. One call site, in the shell. */
export function usePlanLoader() {
  const status = useAuth((s) => s.status);
  useEffect(() => {
    if (status === 'signedIn') void usePlan.getState().load();
    else if (status === 'signedOut') usePlan.getState().reset();
  }, [status]);
}

/** The Stripe account is the company's, not the product's, so the card statement never says Timato. Said wherever money is about to move. */
export const CARD_STATEMENT = 'Charges appear as SIDEWIND, our company, on your card statement.';

export { money } from '@/lib/money';
